import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Button,
  Container,
  Paper,
  Switch,
  FormControlLabel,
  Snackbar,
  Alert,
  CircularProgress,
  Chip,
} from "@mui/material";
import VerifiedRoundedIcon from "@mui/icons-material/VerifiedRounded";
import GppGoodOutlinedIcon from "@mui/icons-material/GppGoodOutlined";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import VideoCallIcon from "@mui/icons-material/VideoCall";
import WorkOutlineRoundedIcon from "@mui/icons-material/WorkOutlineRounded";
import MicNoneRoundedIcon from "@mui/icons-material/MicNoneRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth, api } from "../context/AuthContext";
import heroImage from "../Assests/Images/hero.png";
import RecommendedJobs from "../components/RecommendedJobs";

function CandidateDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [jobAlertEnabled, setJobAlertEnabled] = useState(
    user?.job_alert_subscription !== false
  );

  useEffect(() => {
    if (user) {
      setJobAlertEnabled(user.job_alert_subscription !== false);
    }
  }, [user]);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });

  // Direct interviews
  const [directInterviews, setDirectInterviews] = useState([]);
  const [loadingDirect, setLoadingDirect] = useState(false);

  useEffect(() => {
    setLoadingDirect(true);
    api.get('/direct-interviews/candidate/my')
      .then((res) => setDirectInterviews(res.data.interviews || []))
      .catch(() => {})
      .finally(() => setLoadingDirect(false));
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("action") === "unsubscribe") {
      // Small delay to ensure snackbar doesn't get dismissed by other state changes if any
      const timer = setTimeout(() => {
        handleAutoUnsubscribe();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [location.search]);

  const handleAutoUnsubscribe = async () => {
    setSaving(true);
    try {
      await api.patch("/applications/candidate/preferences/job-alerts", {
        jobAlertSubscription: false,
      });
      setJobAlertEnabled(false);
      setToast({
        open: true,
        message: "You have been successfully unsubscribed from job alerts.",
        severity: "success",
      });
    } catch (error) {
      console.error("Auto-unsubscribe failed:", error);
      setToast({
        open: true,
        message: "Failed to unsubscribe. Please try disabling it manually below.",
        severity: "error",
      });
    } finally {
      setSaving(false);
      // Clean up URL parameters by navigating to the same path without search
      navigate("/candidate/dashboard", { replace: true });
    }
  };

  const handleSavePreferences = async () => {
    setSaving(true);
    try {
      await api.patch("/applications/candidate/preferences/job-alerts", {
        jobAlertSubscription: jobAlertEnabled,
      });
      setToast({
        open: true,
        message: jobAlertEnabled
          ? "Job alerts enabled successfully."
          : "Job alerts disabled successfully.",
        severity: "success",
      });
    } catch {
      setToast({
        open: true,
        message: "Failed to save preferences. Please try again.",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container sx={{ py: 2, minHeight: '100vh' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #0f2027, #203a43, #2c5364)",
          color: "white",
          p: 4,
          borderRadius: "36px",
          mb: 3,
          boxShadow: 3,
        }}
      >
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={7}>
            <Typography
              variant="h1"
              fontWeight={800}
              gutterBottom
              sx={{
                color: "white",
                fontSize: {
                  xs: "2.4rem",
                  sm: "2.8rem",
                  md: "3.2rem",
                  lg: "3.5rem",
                },
                lineHeight: 1.1,
                letterSpacing: "-0.03em",
              }}
            >
              Welcome, {user?.first_name || user?.firstName || "Candidate"}!
            </Typography>
            <Typography
              sx={{
                color: "white",
                opacity: 0.9,
                mb: 2,
                fontSize: {
                  xs: "1.2rem",
                  md: "1.55rem",
                },
                fontWeight: 600,
              }}
            >
              Your personalized dashboard
            </Typography>
            <Typography
              sx={{
                color: "white",
                opacity: 0.8,
                fontSize: "1.15rem",
                lineHeight: 1.8,
              }}
            >
              Access all your applications and interviews in one click.
            </Typography>
          </Grid>
          <Grid item xs={12} md={5}>
            <Box
              component="img"
              src={heroImage}
              alt="Welcome"
              sx={{
                width: "100%",
                maxHeight: 240,
                objectFit: "contain",
                filter: "drop-shadow(0 0 10px rgba(0,255,255,0.3))",
              }}
            />
          </Grid>
        </Grid>
      </Box>



            {/* Apply with Confidence Banner */}

            <Paper
              elevation={0}
              sx={{
                mb: 3,
                px: 4,
                py: 2.5,
                borderRadius: "20px",
                border: "1px solid #93C5FD",
                background:
                  "linear-gradient(90deg, #F8FBFF 0%, #EEF6FF 50%, #F8FBFF 100%)",
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                justifyContent: "space-between",
                alignItems: "center",
                textAlign: { xs: "center", md: "left" },
              }}
            >
              {/* Left Side */}

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2.5,
                }}
              >
                <VerifiedRoundedIcon
                  sx={{
                    fontSize: 54,
                    color: "#2563EB",
                  }}
                />

                <Box>
                  <Typography
                    sx={{
                      fontSize: "1.45rem",
                      fontWeight: 700,
                      color: "#111827",
                      mb: 0.5,
                    }}
                  >
                    Apply with Confidence
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: "1rem",
                      color: "#374151",
                    }}
                  >
                    Apply only{" "}
                    <strong>Blue Tick Verified Company</strong>{" "}
                    job postings for a trusted and secure hiring experience.
                  </Typography>
                </Box>
              </Box>

              {/* Right Side */}

              <GppGoodOutlinedIcon
                sx={{
                  fontSize: 56,
                  color: "#3B82F6",
                }}
              />
            </Paper>



      {/* Quick Actions */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h2" sx={{ mb: 3 }} fontWeight="bold">
                 Quick Actions
                </Typography>

        <Grid
          container
          spacing={2}
          justifyContent="flex-start"
          alignItems="center"
        >

          {/* Applications */}

          <Grid item xs={12} sm={6} md={4}>
            <Paper
              elevation={0}
              onClick={() => navigate("/my-applications")}
              sx={{
                  cursor: "pointer",

                  width: "100%",


                  height: 92,

                  borderRadius: "18px",

                  px: 2,
                  py: 1.5,

                  transition: ".25s",

                  display: "flex",
                  alignItems: "center",

                  boxSizing: "border-box",

                background:
                  "linear-gradient(135deg,#1E3A8A 0%,#22C7E5 100%)",

                color: "#fff",

                transition: ".3s",

                "&:hover": {
                  transform: "translateY(-6px)",
                  boxShadow: "0 20px 45px rgba(0,0,0,.22)"
                }
              }}
            >
<Box
  sx={{
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    height: "100%",
  }}
>
                <Box sx={{ display: "flex", gap: 1.5 }}>

                  <Box
                    sx={{
                      width: 50,
                      height: 50,
                      borderRadius: "14px",
                      border: "1px solid rgba(255,255,255,.75)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "rgba(255,255,255,.08)"
                    }}
                  >
                    <WorkOutlineRoundedIcon sx={{ fontSize: 24 }} />
                  </Box>

                  <Box>

                    <Typography
                      sx={{
                        fontSize: 18,
                        fontWeight: 700
                      }}
                    >
                      My Applications
                    </Typography>

                    <Typography
                      sx={{
                        mt: 1,
                        opacity: .92,
                        fontSize: 15
                      }}
                    >
                      View and track all your job applications
                    </Typography>

                  </Box>

                </Box>

                <ArrowForwardIosRoundedIcon />
              </Box>
            </Paper>
          </Grid>





          {/* Interviews */}

          <Grid item xs={12} sm={6} md={4}>
            <Paper
              elevation={0}
              onClick={() => navigate("/my-interview-history")}
              sx={{
                  cursor: "pointer",

                  width: "100%",


                  height: 92,

                  borderRadius: "18px",

                  px: 2,
                  py: 1.5,

                  transition: ".25s",

                  display: "flex",
                  alignItems: "center",

                  boxSizing: "border-box",

                background:
                  "linear-gradient(135deg,#FF9800 0%,#FFB300 100%)",

                color: "#fff",

                transition: ".3s",

                "&:hover": {
                  transform: "translateY(-6px)",
                  boxShadow: "0 20px 45px rgba(0,0,0,.22)"
                }
              }}
            >
             <Box
               sx={{
                 display: "flex",
                 justifyContent: "space-between",
                 alignItems: "center",
                 width: "100%",
                 height: "100%",
               }}
             >
                <Box sx={{ display: "flex", gap: 1.5 }}>

                  <Box
                    sx={{
                      width: 50,
                      height: 50,
                      borderRadius: "14px",
                      border: "1px solid rgba(255,255,255,.75)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "rgba(255,255,255,.08)"
                    }}
                  >
                    <MicNoneRoundedIcon sx={{ fontSize: 24 }} />
                  </Box>

                  <Box>

                    <Typography
                      sx={{
                        fontSize: 18,
                        fontWeight: 700
                      }}
                    >
                      My Interviews
                    </Typography>

                    <Typography
                      sx={{
                        mt: 1,
                        opacity: .92,
                        fontSize: 15
                      }}
                    >
                      Check your interview schedule and history
                    </Typography>

                  </Box>

                </Box>

                <ArrowForwardIosRoundedIcon />
              </Box>
            </Paper>
          </Grid>

        </Grid>
      </Box>

      {/* Direct Interviews Section */}
      {(loadingDirect || directInterviews.length > 0) && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h2" sx={{ mb: 3 }} fontWeight="bold">
                   Upcoming Interviews
                  </Typography>
          {loadingDirect ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Grid container spacing={2}>
              {directInterviews
                .filter((i) => i.status === "SCHEDULED" || i.status === "STARTED" || i.status === "EXPIRED")
                .map((interview) => {
                  const d = new Date(interview.scheduled_at);
                  const dateStr = d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", timeZone: "Asia/Kolkata" });
                  const timeStr = d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" });
                  const isExpired = interview.status === "EXPIRED";
                  return (
                    <Grid item xs={12} sm={6} md={4} key={interview.id}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2.5,
                          borderRadius: 3,
                          border: isExpired ? "1px solid #E5E7EB" : "1px solid #E0F2FE",
                          background: isExpired
                            ? "linear-gradient(135deg, #F9FAFB 0%, #F3F4F6 100%)"
                            : "linear-gradient(135deg, #F0F9FF 0%, #E0F2FE 100%)",
                          opacity: isExpired ? 0.85 : 1,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                          <VideoCallIcon sx={{ color: isExpired ? "#9CA3AF" : "#0369A1", fontSize: 22 }} />
                          <Typography variant="subtitle1" fontWeight={700} color="text.primary" noWrap>
                            {interview.job_title}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                          <Chip
                            label="Direct Interview"
                            size="small"
                            sx={{ bgcolor: "#DBEAFE", color: "#1D4ED8", fontWeight: 700, fontSize: "0.68rem", height: 20 }}
                          />
                          {isExpired && (
                            <Chip
                              label="Expired"
                              size="small"
                              sx={{ bgcolor: "#FEE2E2", color: "#DC2626", fontWeight: 700, fontSize: "0.68rem", height: 20 }}
                            />
                          )}
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          <strong>Scheduled:</strong> {dateStr}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          <strong>Time:</strong> {timeStr} IST
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          <strong>Duration:</strong> {interview.interview_duration_minutes || 60} Minutes
                        </Typography>
                        {isExpired ? (
                          <Typography
                            variant="body2"
                            color="error"
                            sx={{ fontSize: "0.78rem", textAlign: "center", lineHeight: 1.4 }}
                          >
                            This interview has expired. Please contact the recruiter if you need a new interview link.
                          </Typography>
                        ) : (
                          <Button
                            variant="contained"
                            size="small"
                            fullWidth
                            sx={{
                              borderRadius: 2,
                              fontWeight: 700,
                              background: "linear-gradient(135deg, #0369A1 0%, #0284C7 100%)",
                            }}
                            onClick={() => {
                              if (interview.interview_link) {
                                const url = new URL(interview.interview_link);
                                navigate(url.pathname);
                              } else {
                                navigate("/my-interview-history");
                              }
                            }}
                          >
                            Start Interview
                          </Button>
                        )}
                      </Paper>
                    </Grid>
                  );
                })}
            </Grid>
          )}
        </Box>
      )}

      {/* Recommended Jobs */}
      <RecommendedJobs />

      {/* Job Alert Preferences */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h2" sx={{ mb: 3 }} fontWeight="bold">
          Job Alert Preferences
        </Typography>
        <Paper
          elevation={2}
          sx={{
            p: { xs: 2.5, sm: 4 },
            borderRadius: 3,
            border: "1px solid #E2F0EE",
            background: "#FAFFFE",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
            <NotificationsActiveIcon sx={{ color: "#1E3A8A", fontSize: 26 }} />
            <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
              Email Job Alerts
            </Typography>
          </Box>

          <Typography variant="body2" sx={{ color: "#475569", mb: 3 }}>
            Receive email notifications when recruiters post relevant jobs.
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={jobAlertEnabled}
                onChange={(e) => setJobAlertEnabled(e.target.checked)}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": { color: "#1E3A8A" },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#1E3A8A",
                  },
                }}
              />
            }
            label={
              <Typography variant="body1" fontWeight={500}>
                {jobAlertEnabled ? "On" : "Off"}
              </Typography>
            }
            sx={{ mb: 1 }}
          />

          <Typography
            variant="body2"
            sx={{
              color: jobAlertEnabled ? "#1E40AF" : "#94A3B8",
              mb: 3,
              ml: 0.5,
              fontStyle: "italic",
            }}
          >
            {jobAlertEnabled
              ? "You will receive relevant job opportunities via email."
              : "You will not receive recruiter job notifications."}
          </Typography>

          <Button
            variant="contained"
            onClick={handleSavePreferences}
            disabled={saving}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1.2,
              fontWeight: 600,
              textTransform: "none",
              fontSize: "0.95rem",
              background: "linear-gradient(135deg, #1E3A8A 0%, #1E40AF 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #1E40AF 0%, #1D4ED8 100%)",
              },
              "&.Mui-disabled": { opacity: 0.7 },
              minWidth: 150,
            }}
          >
            {saving ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Save Preferences"
            )}
          </Button>
        </Paper>
      </Box>

      {/* Toast notifications */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          severity={toast.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default CandidateDashboard;
